/**
 * PageErrorBoundary — catches any runtime crash inside a dashboard page.
 * Shows a branded recovery screen instead of a blank page.
 * The "Fix & Recover" button resets the boundary, clears stale cache,
 * and navigates back to a safe dashboard URL.
 *
 * Must be a class component — React Error Boundaries cannot be hooks.
 */
import React from "react";
import { RefreshCw, AlertTriangle, Home } from "lucide-react";

export default class PageErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorId: 0 };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // Log to console for devs — avoid exposing to end users
        console.error("[PageErrorBoundary] Page crash:", error, info?.componentStack);
    }

    recover = () => {
        // 1. Clear any react-query / SWR / in-memory caches stored in sessionStorage
        try {
            const keysToRemove = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const k = sessionStorage.key(i);
                if (k && (k.startsWith("rq:") || k.startsWith("swr:") || k.startsWith("law_os_cache:"))) {
                    keysToRemove.push(k);
                }
            }
            keysToRemove.forEach(k => sessionStorage.removeItem(k));
        } catch (_) { /* silent */ }

        // 2. Reset the boundary — bump errorId so React remounts the child tree fresh
        this.setState({ hasError: false, error: null, errorId: this.state.errorId + 1 });

        // 3. Navigate to a safe root (the mode prop tells us where)
        const safe = this.props.safeRoute || "/app";
        if (window.location.pathname !== safe) {
            window.location.href = safe;
        }
    };

    reload = () => {
        window.location.reload();
    };

    render() {
        if (!this.state.hasError) {
            return (
                <React.Fragment key={this.state.errorId}>
                    {this.props.children}
                </React.Fragment>
            );
        }

        const msg = this.state.error?.message || "An unexpected error occurred.";
        const isDev = process.env.NODE_ENV === "development";

        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 text-center"
                data-testid="page-error-boundary">

                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl bg-destructive/10 border border-destructive/30
                    flex items-center justify-center mb-6">
                    <AlertTriangle className="w-6 h-6 text-destructive" strokeWidth={1.5}/>
                </div>

                {/* Heading */}
                <h2 className="text-xl font-semibold text-foreground mb-2">
                    This page encountered an error
                </h2>
                <p className="text-sm text-muted-foreground max-w-sm mb-1">
                    Something went wrong while loading this section.
                    Your data is safe — this is a display issue only.
                </p>

                {/* Dev-only error detail */}
                {isDev && msg && (
                    <p className="text-[11px] font-mono text-destructive/80 bg-destructive/5 border border-destructive/20
                        rounded px-3 py-1.5 max-w-md mt-3 mb-3 text-left break-all">
                        {msg}
                    </p>
                )}

                {/* Action buttons */}
                <div className="flex items-center gap-3 mt-6">
                    <button
                        onClick={this.recover}
                        data-testid="error-boundary-recover-btn"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg
                            bg-primary text-primary-foreground text-sm font-medium
                            hover:bg-primary/90 transition-colors">
                        <Home className="w-4 h-4"/>
                        Fix &amp; Recover
                    </button>

                    <button
                        onClick={this.reload}
                        data-testid="error-boundary-reload-btn"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg
                            border border-border text-sm text-muted-foreground
                            hover:border-primary hover:text-foreground transition-colors">
                        <RefreshCw className="w-4 h-4"/>
                        Reload Page
                    </button>
                </div>

                <p className="text-[11px] text-muted-foreground mt-5">
                    "Fix &amp; Recover" clears cached state and takes you to your dashboard.
                </p>
            </div>
        );
    }
}
